import { EmptyState } from "@/components/elements/EmptyState";
import { usePermission } from "@/features/roleAccess";
import { Outlet } from "@tanstack/react-location";
import { useFlags } from "launchdarkly-react-client-sdk";

import { SettingsPageLayout } from "../PageLayout";

/**
 * Gates the whole area, not just its buttons: `accessGroups` sits behind the same
 * admin-only policy as the mutations, so a query mounted for a non-admin fails the
 * page load rather than rendering an empty list. As the route `element`, this keeps
 * the pages — and their queries — from mounting at all.
 *
 * The flag is checked before the permission so an admin in a workspace without the
 * feature is told it is unavailable rather than that they lack access. An absent
 * LaunchDarkly flag reads as `undefined`, so the feature stays hidden until the flag
 * exists and is switched on.
 */
export function AccessGroupsGuard() {
  const { dataAccessControlEnabled } = useFlags();
  const canManage = usePermission("manageAccessGroups");

  if (!dataAccessControlEnabled) {
    return (
      <SettingsPageLayout title="Access groups">
        <EmptyState
          header="Access groups aren't available in this workspace"
          description="Data access control is still rolling out. Contact Weld support if you need it enabled."
        />
      </SettingsPageLayout>
    );
  }

  if (!canManage) {
    return (
      <SettingsPageLayout title="Access groups">
        <EmptyState
          header="You don't have access to this page"
          description="Only workspace admins can manage Access groups."
        />
      </SettingsPageLayout>
    );
  }

  return <Outlet />;
}
